import { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Col } from 'antd';
import currency from 'currency.js';
import { toast } from 'react-toastify';
import MailchimpSubscribe from 'react-mailchimp-subscribe';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { useQuery } from 'v3/hooks/useQuery';
import { useMixpanel } from 'v3/hooks/useMixpanel';
import { DealsClient, DownPaymentResult } from 'v3/api/client/DealsClient';
import { parseCurrency } from 'v3/utils/parse';
import { setLeadData } from 'leadData/leadSlice';

import Layout from 'v3/templates/Layout';
import Hero from 'v3/components/Hero';
import SidebarBox from 'v3/components/SidebarBox';
import Tag from 'v3/components/Tag';
import Tabs from 'v3/components/Tabs';
import IconItem from 'v3/components/IconItem';
import DealBox from 'v3/components/DealBox';
import Loading from 'v3/components/Loading';
import Select from 'v3/components/Select';
import Field from 'v3/components/Field';
import Button from 'v3/components/Button';
import CounterInput from 'v3/components/CounterInput';
import Empty from 'v3/components/Empty';
import Banner from 'v3/components/Banner';
import ComparisonModal, {
  ComparisonModalProps,
} from 'v3/components/ComparisonModal';
import VideoModal from 'v3/components/VideoModal';
import IncreaseChancesModal from 'v3/components/IncreaseChancesModal';
import DiscountToast from 'v3/components/DiscountToast';

import heroImg from 'v3/assets/images/man.webp';
import flexibleTermsIco from 'v3/assets/svg/flexible-terms-ico.svg';
import noCreditCheckIco from 'v3/assets/svg/no-credit-check-ico.svg';
import noHiddenFeesIco from 'v3/assets/svg/no-hidden-fees-ico.svg';
import lowEAPRIco from 'v3/assets/svg/low-eapr-ico.svg';
import offerFlagIco from 'v3/assets/svg/offer-flag-ico.svg';

import hero2025Img from 'v3/assets/images/hero2025.png';
import downPaymentIcon from 'v3/assets/svg/down-payment-icon.svg';
import closedEyesIcon from 'v3/assets/svg/closed-eyes-icon.svg';
import moneyVoidedIcon from 'v3/assets/svg/money-voided-icon.svg';
import noSSNRequiredIcon from 'v3/assets/svg/no-ssn-required-icon.svg';
import percentageSpeedometerIcon from 'v3/assets/svg/percentage-speedometer-icon.svg';

import bannerImageAutoHalfPayment from 'v3/assets/images/auto-half-payment.png';
import bannerImageMortgateHalfPayment from 'v3/assets/images/mortgage-half-payment.png';
import { DollarSign } from '@styled-icons/fa-solid/DollarSign';
import { ExchangeDollar } from '@styled-icons/remix-line/ExchangeDollar';
import { Mail } from '@styled-icons/remix-line';
import modalBackgroundImage from 'v3/assets/images/video-background.png';

import theme from 'styles/theme';
import * as S from './styles';
import Radio from 'v3/components/Radio';
import { monthsArray } from 'v3/components/Range';
import {
  DEAL_VALUE_RANGE_MESSAGE,
  isDealValueInRange,
} from 'v3/utils/dealValue';

const autoSimulateSchema = Yup.object().shape({
  vehiclePrice: Yup.string().test(
    'dealValue',
    DEAL_VALUE_RANGE_MESSAGE,
    (value) => (value ? isDealValueInRange(parseCurrency(value)) : false)
  ),
  downPayment: Yup.string()
    .test(
      'downPayment',
      'The down payment must be greater or equal to $500',
      (value: any) => {
        if (value) {
          return parseCurrency(value) === 0 || parseCurrency(value) >= 500;
        }

        return true;
      }
    )
    .when('vehiclePrice', (vehiclePrice: string, schema: Yup.AnySchema) => {
      return schema.test(
        'compare values',
        'The down payment must be less than the vehicle price',
        (value: any) => {
          if (value && vehiclePrice) {
            return parseCurrency(value) <= parseCurrency(vehiclePrice);
          }

          return true;
        }
      );
    }),
});

const realStateSimulateSchema = Yup.object().shape({
  vehiclePrice: Yup.string().test(
    'dealValue',
    'The value must be between $100,000 and $1,000,000',
    (value) => {
      if (value) {
        return (
          parseCurrency(value) >= 100000 && parseCurrency(value) <= 1000000
        );
      }

      return false;
    }
  ),
  downPayment: Yup.string()
    .test(
      'downPayment',
      'The down payment must be greater or equal to $500',
      (value: any) => {
        if (value) {
          return parseCurrency(value) === 0 || parseCurrency(value) >= 500;
        }

        return true;
      }
    )
    .when('vehiclePrice', (vehiclePrice: string, schema: Yup.AnySchema) => {
      return schema.test(
        'compare values',
        'The down payment must be less than the vehicle price',
        (value: any) => {
          if (value && vehiclePrice) {
            return parseCurrency(value) <= parseCurrency(vehiclePrice);
          }

          return true;
        }
      );
    }),
});

const laterSchema = Yup.object().shape({
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
});

type initialValuesProps = {
  creditScore: number;
  vehiclePrice: number;
  loanTerms: keyof typeof monthsArray;
  downPayment: string;
  wantCar: 'later' | 'now';
};

const DealsPage = () => {
  const query = useQuery();

  const initialPropertyType =
    query.get('property') === 'real-estate' ? 'realEstate' : 'auto';

  const [autoOrRealEstate, setAutoOrRealEstate] = useState(initialPropertyType);

  const initialTerms = query.get('n_months')
    ? (Number(query.get('n_months')) as initialValuesProps['loanTerms'])
    : 84;

  const autoInitialTerms =
    query.get('n_months') && query.get('property') === 'auto'
      ? (Number(query.get('n_months')) as initialValuesProps['loanTerms'])
      : 84;

  const realStateInitialTerms =
    query.get('n_months') && initialPropertyType === 'realEstate'
      ? (Number(query.get('n_months')) as initialValuesProps['loanTerms'])
      : 360;

  const autoInitialPrice =
    query.get('amount') && query.get('property') === 'auto'
      ? Number(query.get('amount'))
      : 30000;

  const realStateInitialPrice =
    query.get('amount') && initialPropertyType === 'realEstate'
      ? Number(query.get('amount'))
      : 200000;

  const initialCreditScore = query.get('credit_score')
    ? Number(query.get('credit_score'))
    : 670;
  // const initialPaymentOption = query.get('paymentOption') ?? 'half';
  const [clubId, setClubId] = useState('');
  const [creditScore, setCreditScore] = useState(initialCreditScore);
  const [creditScoreCaption, setCreditScoreCaption] = useState('Good');
  const [price, setPrice] = useState(
    initialPropertyType === 'auto' ? autoInitialPrice : realStateInitialPrice
  );
  const [vehiclePrice, setVehiclePrice] = useState(price);
  const [realStatePrice, setRealStatePrice] = useState(price);
  const [terms, setTerms] = useState<number>(
    initialPropertyType === 'auto' ? autoInitialTerms : realStateInitialTerms
  );
  const [downPayment, setDownPayment] = useState('0');
  const [wantCar, setWantCar] = useState('I can wait and save more');
  const [wantCarSection, setWantCarSection] = useState('later');
  const [paymentOption, setPaymentOption] = useState("full");
  const [halfPayment, setHalfPayment] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [initialMonthlyPayment, setInitialMonthlyPayment] = useState(0);
  const [totalSavings, setTotalSavings] = useState(0);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showChancesModal, setShowChancesModal] = useState(false);
  const [fullFirstValue, setFullFirstValue] = useState(0);
  const [fullLastValue, setFullLastValue] = useState(0);
  const [halfFirstValue, setHalfFirstValue] = useState(0);
  const [halfLastValue, setHalfLastValue] = useState(0);
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);
  const [comparisonModalData, setComparisonModalData] = useState(
    {} as {
      chartData: ComparisonModalProps['chartData'];
      tableData: ComparisonModalProps['tableData'];
    }
  );
  const [valueIncreased, setValueIncreased] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downPaymentData, setDownPaymentData] =
    useState<DownPaymentResult | undefined>();
  const mp = useMixpanel();
  const { wasDiscountShown } = useSelector((state: RootState) => state.lead);
  const dispatch = useDispatch();

  const autoInitialValues: initialValuesProps = {
    creditScore: initialCreditScore,
    vehiclePrice: autoInitialPrice,
    loanTerms: initialTerms,
    downPayment: '0',
    wantCar: 'later',
  };

  const realStateInitialValues: initialValuesProps = {
    creditScore: initialCreditScore,
    vehiclePrice: realStateInitialPrice,
    loanTerms: realStateInitialTerms,
    downPayment: '0',
    wantCar: 'later',
  };

  const autoFormikRef = useRef<FormikProps<typeof autoInitialValues>>(null);
  const realStateFormikRef =
    useRef<FormikProps<typeof realStateInitialValues>>(null);

  useEffect(() => {
    async function getDealsValuesInfo() {
      setLoading(true);
      const newPrice = parseCurrency(String(price));

      // A failed request returns `{ error }` with no data, so there may be no deal to read.
      const { data } = await DealsClient().getDealsInfo(
        newPrice,
        terms,
        creditScore,
        parseCurrency(downPayment),
        valueIncreased ? initialMonthlyPayment + valueIncreased : undefined
      );
      const deal = data?.[0];

      setTimeout(() => {
        if (deal) {
          if (!valueIncreased) {
            setInitialMonthlyPayment(deal.monthlyPayment);
          }
          setVehiclePrice(Math.ceil(deal.amount));
          setMonthlyPayment(deal.monthlyPayment);
          setTotalSavings(deal.totalSavings);
          setHalfPayment(deal.halfPayment);
        }

        setLoading(false);
      }, 1500);
    }

    getDealsValuesInfo();
  }, [price, terms, creditScore, clubId, downPayment, valueIncreased]);

  useEffect(() => {
    async function getDealSimulation() {
      const newPrice = valueIncreased
        ? vehiclePrice
        : parseCurrency(String(price));
      const { data } = await DealsClient().simulateDeal({
        amount: newPrice,
        terms,
        creditScore,
        downPayment: parseCurrency(downPayment),
      });

      if (data) {
        setComparisonModalData({
          tableData: {
            sc: { ...data, fees: data.eApr },
            traditional: {
              costOfCredit: data.mCostOfCredit,
              downPayment: data.downPayment,
              fees: data.apr,
              totalCost: data.mTotalCost,
              totalSavings: data.mTotalSavings,
            },
          },
          chartData: data,
        });
      }
    }

    async function matchDealWithGroup() {
      const newPrice = valueIncreased
        ? vehiclePrice
        : parseCurrency(String(price));

      const { data } = await DealsClient().dealClubMatch(newPrice);

      if (data) {
        setClubId(data.id);
      }
    }

    async function getDealsRangeInfo() {
      const newPrice = valueIncreased
        ? vehiclePrice
        : parseCurrency(String(price));

      const { data } = await DealsClient().getDealRanges(
        creditScore,
        newPrice,
        terms,
        parseCurrency(downPayment)
      );

      if (data) {
        setFullFirstValue(data.dataFull[0]);
        setFullLastValue(data.dataFull[1]);
        setHalfFirstValue(data.dataHalf[0]);
        setHalfLastValue(data.dataHalf[1]);
      }
    }

    async function getDownPaymentData() {
      const newPrice = valueIncreased
        ? vehiclePrice
        : parseCurrency(String(price));

      const { data } = await DealsClient().getDownPaymentInfo({
        creditScore,
        amount: newPrice,
        terms,
      });

      if (data) {
        setDownPaymentData(data);
      }
    }

    getDealSimulation();
    matchDealWithGroup();
    getDealsRangeInfo();
    getDownPaymentData();
  }, [creditScore, downPayment, price, terms, valueIncreased, vehiclePrice]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.matchMedia('(max-width: 992px)').matches);
    };

    window.addEventListener('resize', checkScreenSize);

    checkScreenSize();

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  type dictionaryTypes = {
    [key: string]: string;
  };

  const CREDIT_SCORE: dictionaryTypes = {
    '800': 'Exceptional (800)',
    '740': 'Very Good (740 - 799)',
    '670': 'Good (670 - 739)',
    '580': 'Fair (580 - 669)',
    '450': 'Poor (300 - 579)',
    '300': "I don't have one",
  };

  const AUTO_OR_REAL_ESTATE: dictionaryTypes = {
    auto: 'Auto',
    realEstate: 'Real Estate',
  };

  const LOAN_TERMS = {
    '96': '96',
    '84': '84',
    '72': '72',
    '60': '60',
    '48': '48',
    '36': '36',
    '24': '24',
  };

  const LOAN_TERMS_REAL_STATE = {
    '360': '360',
    '180': '180',
  };

  const WANT_CAR: dictionaryTypes = {
    later: 'I can wait and save more',
    now: 'Right now and save less',
  };

  const PAYMENT_OPTION: dictionaryTypes = {
    '1': 'full',
    '2': 'half',
  };

  const onApplyDownPaymentClick = (downPaymentValue: number) => {
    if (autoFormikRef.current) {
      autoFormikRef.current.setFieldValue(
        'downPayment',
        String(downPaymentValue)
      );
      autoFormikRef.current.submitForm();
      setShowChancesModal(false);
    }
  };

  const paymentOptionSelected = (index: string) => {
    setPaymentOption(PAYMENT_OPTION[index]);
  };

  const TooltipText = (paymentType: string) => {
    if (paymentType === 'half') {
      return '*The number of months to get the car may vary depending on a lot of factors, value of club, number of members inside the club etc';
    } else {
      return 'With good payment habits, you could receive your car within the first 30 months. Everyone receives their car by the end of the contract term if all payments have been made.';
    }
  };

  // const showToastIn10Seconds = () => {
  //   setTimeout(
  //     () =>
  //       toast(<DiscountToast />, {
  //         autoClose: false,
  //         containerId: 'customPosition',
  //         closeOnClick: false,
  //       }),
  //     10000
  //   );
  // };

  return (
    <Layout>
      <ComparisonModal
        open={showComparisonModal}
        closeModal={() => setShowComparisonModal(false)}
        {...comparisonModalData}
      />

      <IncreaseChancesModal
        open={showChancesModal}
        onCancel={() => setShowChancesModal(false)}
        tooltip={TooltipText(paymentOption)}
        terms={terms as any}
        downPaymentData={
          downPaymentData ?? {
            '1000': {
              newMonthlyPayment: monthlyPayment,
              userChanceRange: [fullFirstValue, fullLastValue],
            },
          }
        }
        vehiclePrice={vehiclePrice}
        onApplyDownPayment={onApplyDownPaymentClick}
      />

      <Hero
        img={hero2025Img}
        alt="Image of a happy family celebrating"
        title={
          <>
            Browse your exclusive
            <br />
            <S.HeroTitleHighlight>Auto & Real Estate</S.HeroTitleHighlight>
            <br />
            savings deal.
          </>
        }
        optionsList={[
          {
            icon: <img src={moneyVoidedIcon} alt="" />,
            text: (
              <p>
                No{' '}
                <strong>
                  hidden
                  <br /> fees
                </strong>{' '}
                <br />
              </p>
            ),
          },
          {
            icon: <img src={percentageSpeedometerIcon} alt="" />,
            text: (
              <p>
                No{' '}
                <strong>
                  Credit
                  <br /> Check
                </strong>
              </p>
            ),
          },
          {
            icon: <img src={closedEyesIcon} alt="" />,
            text: (
              <p>
                No{' '}
                <strong>
                  Origination
                  <br />
                  fees
                </strong>
              </p>
            ),
          },
          {
            icon: <img src={noSSNRequiredIcon} alt="" />,
            text: (
              <p>
                No{' '}
                <strong>
                  SSN
                  <br />
                  required
                </strong>
              </p>
            ),
          },
          {
            icon: <img src={downPaymentIcon} alt="" />,
            text: (
              <p>
                No{' '}
                <strong>
                  Down
                  <br />
                  Payments
                </strong>
              </p>
            ),
          },
        ]}
      />

      <S.Wrapper>
        <S.Container>
          <S.Row justify="space-between">
            <S.Col xs={24} xl={6}>
              <SidebarBox as="aside">
                <S.FormTitle>Simulate your deal</S.FormTitle>

                <Tabs
                  activeTab={autoOrRealEstate}
                  style={{ width: '100%', marginBottom: `${24 / 16}rem` }}
                  tabs={[
                    {
                      title: 'Auto',
                      index: 'auto',
                    },
                    {
                      title: 'Real Estate',
                      index: 'realEstate',
                    },
                  ]}
                  onTabChange={(index) => setAutoOrRealEstate(index)}
                />

                <div
                  style={
                    autoOrRealEstate !== 'realEstate'
                      ? {
                          width: '0px',
                          height: 0,
                          overflow: 'hidden',
                          position: 'absolute',
                          top: 0,
                        }
                      : { backgroundColor: '' }
                  }
                >
                  <Formik
                    initialValues={realStateInitialValues}
                    validationSchema={realStateSimulateSchema}
                    onSubmit={(values: initialValuesProps) => {
                      const newCreditScore =
                        CREDIT_SCORE[values.creditScore].split('(')[0];
                      const newWantCar = WANT_CAR[values.wantCar];

                      mp.fireCalculateDeal({ ...values, wantCar: newWantCar });

                      setCreditScore(values.creditScore);
                      setCreditScoreCaption(newCreditScore);
                      setPrice(values.vehiclePrice); // to be removed
                      setRealStatePrice(values.vehiclePrice);
                      setTerms(values.loanTerms);
                      setDownPayment(values.downPayment);
                      setWantCar(newWantCar);
                      setWantCarSection(values.wantCar);
                      setValueIncreased(0);

                      // if (!wasDiscountShown) {
                      //   showToastIn10Seconds();
                      //   dispatch(setLeadData({ wasDiscountShown: true }));
                      // }
                    }}
                    innerRef={realStateFormikRef}
                  >
                    {(formik) => {
                      const { errors, touched, isValid } = formik;
                      return (
                        <S.Form>
                          <Select
                            variant="secondary"
                            name="creditScore"
                            label="My Credit Score"
                            options={Object.entries(CREDIT_SCORE).reverse()}
                          />
                          {isMobile ? (
                            <>
                              <S.FieldsWrapper>
                                <Field
                                  variant="secondary"
                                  name="vehiclePrice"
                                  label="House Price"
                                  prefix={<DollarSign width={18} height={18} />}
                                  currency
                                  feedbackMessage={
                                    touched.vehiclePrice && errors.vehiclePrice
                                  }
                                  isValid={
                                    !(
                                      touched.vehiclePrice &&
                                      errors.vehiclePrice
                                    )
                                  }
                                />

                                <Field
                                  variant="secondary"
                                  name="downPayment"
                                  label="Down Payment"
                                  prefix={<DollarSign width={18} height={18} />}
                                  currency
                                  tooltip="Make a down payment and improve your chance to get a car early."
                                  feedbackMessage={
                                    touched.downPayment && errors.downPayment
                                  }
                                  isValid={
                                    !(touched.downPayment && errors.downPayment)
                                  }
                                />
                              </S.FieldsWrapper>
                              <div>
                                <S.RadioLabel>Loan Term (Years)</S.RadioLabel>
                                <S.TermsWrapper>
                                  {Object.entries(LOAN_TERMS_REAL_STATE).map(
                                    ([value]) => (
                                      <Radio
                                        key={value}
                                        name="loanTerms"
                                        value={value}
                                        text={(parseInt(value) / 12).toString()}
                                      />
                                    )
                                  )}
                                </S.TermsWrapper>
                              </div>
                            </>
                          ) : (
                            <>
                              <Field
                                variant="secondary"
                                name="vehiclePrice"
                                label="Property Price"
                                prefix={<DollarSign width={18} height={18} />}
                                currency
                                feedbackMessage={
                                  touched.vehiclePrice && errors.vehiclePrice
                                }
                                isValid={
                                  !(touched.vehiclePrice && errors.vehiclePrice)
                                }
                              />

                              <Select
                                variant="secondary"
                                name="loanTerms"
                                label="Loan Term (Years)"
                                options={Object.entries(LOAN_TERMS_REAL_STATE)
                                  .reverse()
                                  .map(([value]) => [
                                    value,
                                    (parseInt(value) / 12).toString(),
                                  ])}
                              />

                              <Field
                                variant="secondary"
                                name="downPayment"
                                label="Down Payment"
                                prefix={<DollarSign width={18} height={18} />}
                                currency
                                tooltip="Make a down payment and improve your chance to get a car early."
                                feedbackMessage={
                                  touched.downPayment && errors.downPayment
                                }
                                isValid={
                                  !(touched.downPayment && errors.downPayment)
                                }
                              />
                            </>
                          )}

                          <Select
                            variant="secondary"
                            name="wantCar"
                            label="When do you want your property?"
                            options={Object.entries(WANT_CAR)}
                          />

                          <Button disabled={!isValid} fullWidth type="submit">
                            Find deals
                          </Button>
                        </S.Form>
                      );
                    }}
                  </Formik>
                </div>

                <div
                  style={
                    autoOrRealEstate !== 'auto'
                      ? {
                          width: '0px',
                          height: 0,
                          overflow: 'hidden',
                          position: 'absolute',
                          top: 0,
                        }
                      : { backgroundColor: '' }
                  }
                >
                  <Formik
                    initialValues={autoInitialValues}
                    validationSchema={autoSimulateSchema}
                    onSubmit={(values: initialValuesProps) => {
                      const newCreditScore =
                        CREDIT_SCORE[values.creditScore].split('(')[0];
                      const newWantCar = WANT_CAR[values.wantCar];

                      mp.fireCalculateDeal({ ...values, wantCar: newWantCar });

                      setCreditScore(values.creditScore);
                      setCreditScoreCaption(newCreditScore);
                      setPrice(values.vehiclePrice);
                      setVehiclePrice(values.vehiclePrice);
                      setTerms(values.loanTerms);
                      setDownPayment(values.downPayment);
                      setWantCar(newWantCar);
                      setWantCarSection(values.wantCar);
                      setValueIncreased(0);

                      // if (!wasDiscountShown) {
                      //   showToastIn10Seconds();
                      //   dispatch(setLeadData({ wasDiscountShown: true }));
                      // }
                    }}
                    innerRef={autoFormikRef}
                  >
                    {(formik) => {
                      const { errors, touched, isValid } = formik;
                      return (
                        <S.Form>
                          <Select
                            variant="secondary"
                            name="creditScore"
                            label="My Credit Score"
                            options={Object.entries(CREDIT_SCORE).reverse()}
                          />
                          {isMobile ? (
                            <>
                              <S.FieldsWrapper>
                                <Field
                                  variant="secondary"
                                  name="vehiclePrice"
                                  label="Vehicle Price"
                                  prefix={<DollarSign width={18} height={18} />}
                                  currency
                                  feedbackMessage={
                                    touched.vehiclePrice && errors.vehiclePrice
                                  }
                                  isValid={
                                    !(
                                      touched.vehiclePrice &&
                                      errors.vehiclePrice
                                    )
                                  }
                                />

                                <Field
                                  variant="secondary"
                                  name="downPayment"
                                  label="Down Payment"
                                  prefix={<DollarSign width={18} height={18} />}
                                  currency
                                  tooltip="Make a down payment and improve your chance to get a car early."
                                  feedbackMessage={
                                    touched.downPayment && errors.downPayment
                                  }
                                  isValid={
                                    !(touched.downPayment && errors.downPayment)
                                  }
                                />
                              </S.FieldsWrapper>
                              <div>
                                <S.RadioLabel>Loan Term (Months)</S.RadioLabel>
                                <S.TermsWrapper>
                                  {Object.entries(LOAN_TERMS)
                                    .reverse()
                                    .map(([value]) => (
                                      <Radio
                                        key={value}
                                        name="loanTerms"
                                        value={value}
                                      />
                                    ))}
                                </S.TermsWrapper>
                              </div>
                            </>
                          ) : (
                            <>
                              <Field
                                variant="secondary"
                                name="vehiclePrice"
                                label="Vehicle Price"
                                prefix={<DollarSign width={18} height={18} />}
                                currency
                                feedbackMessage={
                                  touched.vehiclePrice && errors.vehiclePrice
                                }
                                isValid={
                                  !(touched.vehiclePrice && errors.vehiclePrice)
                                }
                              />

                              <Select
                                variant="secondary"
                                name="loanTerms"
                                label="Loan Term (Months)"
                                options={Object.entries(LOAN_TERMS).reverse()}
                              />

                              <Field
                                variant="secondary"
                                name="downPayment"
                                label="Down Payment"
                                prefix={<DollarSign width={18} height={18} />}
                                currency
                                tooltip="Make a down payment and improve your chance to get a car early."
                                feedbackMessage={
                                  touched.downPayment && errors.downPayment
                                }
                                isValid={
                                  !(touched.downPayment && errors.downPayment)
                                }
                              />
                            </>
                          )}

                          <Select
                            variant="secondary"
                            name="wantCar"
                            label="When do you want your car?"
                            options={Object.entries(WANT_CAR)}
                          />

                          <Button disabled={!isValid} fullWidth type="submit">
                            Find deals
                          </Button>
                        </S.Form>
                      );
                    }}
                  </Formik>
                </div>
              </SidebarBox>
            </S.Col>

            <S.Col xs={24} xl={16}>
              <S.Content>
                {/* <S.TagsRow>
                  <Tag>Credit Score: {creditScoreCaption}</Tag>
                  <Tag>Price: {currency(price, { precision: 0 }).format()}</Tag>
                  <Tag>{terms} months</Tag>
                  {parseCurrency(downPayment) > 0 && (
                    <Tag>
                      Down Payment:{' '}
                      {currency(downPayment, { precision: 0 }).format()}
                    </Tag>
                  )}
                  <Tag>{wantCar}</Tag>
                </S.TagsRow> */}

                {wantCarSection === 'now' && (
                  <MailchimpSubscribe
                    url="https://xnv.us10.list-manage.com/subscribe/post?u=2b6974dfabb899d0db79cf0c8&amp;id=8a911bd41c&amp;f_id=0062d2e5f0"
                    render={({ subscribe, status }) => (
                      <Formik
                        initialValues={{
                          email: '',
                        }}
                        validationSchema={laterSchema}
                        onSubmit={async ({ email }, actions) => {
                          await subscribe({ EMAIL: email });
                          if (status === 'error') {
                            actions.setFieldError(
                              'email',
                              'An error ocurred. Please try again later.'
                            );
                          } else {
                            actions.resetForm();
                            mp.fireNewsletterSubscription(email);
                            toast.success('Email subscribed successfully!');
                          }
                        }}
                      >
                        {(formik) => {
                          const { errors, touched, isValid } = formik;

                          return (
                            <S.WantCarContainer>
                              <S.FormWantCar>
                                <S.WantCarNowSection>
                                  <Empty
                                    title="We don't have any offers at the moment..."
                                    text="But we can let you know when we find one."
                                  />

                                  <S.EmailWrapper>
                                    <Field
                                      name="email"
                                      placeholder="Email"
                                      rightContent={
                                        <Button
                                          variant="invert"
                                          size="small"
                                          type="submit"
                                          disabled={!isValid}
                                        >
                                          Notify me
                                        </Button>
                                      }
                                      prefix={
                                        <Mail
                                          color={theme.colors.neutralSilver50}
                                          width={26}
                                          height={19.75}
                                        />
                                      }
                                      feedbackMessage={
                                        touched.email && errors.email
                                      }
                                      isValid={!(touched.email && errors.email)}
                                    />
                                  </S.EmailWrapper>
                                </S.WantCarNowSection>

                                <S.Divider>
                                  <span>or</span>
                                </S.Divider>

                                <S.OtherDeals>
                                  <S.OtherDealsText>
                                    If you can afford to wait, we have other
                                    offers for you!
                                  </S.OtherDealsText>

                                  <Button
                                    type="button"
                                    onClick={() => {
                                      // autoFormikRef?.current?.setFieldValue(
                                      //   'wantCar',
                                      //   'later'
                                      // );
                                      // autoFormikRef?.current?.submitForm();
                                    }}
                                  >
                                    Check out other deals
                                  </Button>
                                </S.OtherDeals>
                              </S.FormWantCar>
                            </S.WantCarContainer>
                          );
                        }}
                      </Formik>
                    )}
                  />
                )}

                {wantCarSection === 'later' && (
                  <>
                    {/* <Tabs
                      activeTab={paymentOption === 'half' ? '2' : '1'}
                      tabs={[
                        {
                          title: 'Full Payment',
                          index: '1',
                        },
                        {
                          title: 'Half Payment',
                          index: '2',
                        },
                      ]}
                      onTabChange={(index) => paymentOptionSelected(index)}
                    /> */}

                    <S.DealSection>
                      {/* <S.BannerSection
                        visibility={paymentOption === 'half'}
                        aria-hidden={paymentOption !== 'half'}
                      >
                        {autoOrRealEstate === 'realEstate' ? (
                          <Banner
                            title="You choose Half Payments"
                            text={
                              <>
                                Pay <strong>just half your monthly</strong> rate
                                until <br /> you're awarded your{' '}
                                <a
                                  href="https://savings.club/blog/asked_questions/what-is-a-credit-voucher/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  credit voucher
                                </a>
                                .
                              </>
                            }
                            onClick={() => setIsOpenVideoModal(true)}
                            buttonText="Learn with a video"
                            image={bannerImageMortgateHalfPayment}
                            mobileText="Do you now how the half payment works?"
                            mobileTitle="Learn"
                          />
                        ) : (
                          <Banner
                            title="You choose Half Payments"
                            text={
                              <>
                                Pay <strong>just half your monthly</strong> rate
                                until <br /> you're awarded your{' '}
                                <a
                                  href="https://savings.club/blog/asked_questions/what-is-a-credit-voucher/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  credit voucher
                                </a>
                                .
                              </>
                            }
                            onClick={() => setIsOpenVideoModal(true)}
                            buttonText="Learn with a video"
                            image={bannerImageAutoHalfPayment}
                            mobileText="Do you now how the half payment works?"
                            mobileTitle="Learn"
                          />
                        )}
                      </S.BannerSection> */}

                      <VideoModal
                        title={
                          <>
                            Learn now how <br />
                            half payment works
                          </>
                        }
                        text="Savings Club a New Way To Buy Your Car"
                        videoLink="https://player.vimeo.com/video/1055352100?h=d6153c913d&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                        videoType="vimeo"
                        closeModal={() => setIsOpenVideoModal(false)}
                        open={isOpenVideoModal}
                        backgroundImage={modalBackgroundImage}
                      />

                      {/* {paymentOption === 'half' ? (
                        <S.DealHeader>
                          <IconItem
                            icon={<img src={offerFlagIco} alt="" />}
                            text={<S.OfferTitle>Half-payment</S.OfferTitle>}
                          />
                        </S.DealHeader>
                      ) : null} */}

                      <Loading size="large" load={loading}>
                        <DealBox
                          dealType={autoOrRealEstate as 'auto' | 'realEstate'}
                          save={totalSavings}
                          monthlyPayment={monthlyPayment}
                          border={paymentOption === 'half'}
                          halfPayment={
                            paymentOption === 'half' ? halfPayment : undefined
                          }
                          loanAmount={vehiclePrice}
                          tooltip={TooltipText(paymentOption)}
                          terms={terms as any}
                          firstValue={
                            paymentOption === 'half'
                              ? halfFirstValue
                              : fullFirstValue
                          }
                          lastValue={
                            paymentOption === 'half'
                              ? halfLastValue
                              : fullLastValue
                          }
                          improveChances={() => setShowChancesModal(true)}
                          pricingDetails={() => setShowComparisonModal(true)}
                          cfLink={`/?clubId=${clubId}&dealValue=${vehiclePrice}&paymentOption=${paymentOption}&months=${terms}&downPayment=${parseCurrency(
                            downPayment
                          )}`}
                        />
                      </Loading>
                    </S.DealSection>

                    <S.IncreaseValueSection>
                      <IconItem
                        icon={
                          <ExchangeDollar aria-label="dollar-icon" size={20} />
                        }
                        text={
                          <S.IncreaseValueText>
                            <strong>Pay more to increase the value</strong>
                            <br />
                            The new price will be reflected in your offer.
                          </S.IncreaseValueText>
                        }
                      />

                      <CounterInput
                        min={0}
                        max={360}
                        shouldCleanValue={valueIncreased === 0}
                        formatter={(value) => {
                          if (paymentOption === 'half') {
                            return value / 2;
                          }

                          return value;
                        }}
                        amountToIncrement={30}
                        onChange={(value) => {
                          setValueIncreased(value);
                        }}
                      />
                    </S.IncreaseValueSection>
                  </>
                )}
              </S.Content>
            </S.Col>
          </S.Row>
        </S.Container>
      </S.Wrapper>
    </Layout>
  );
};

export default DealsPage;
